import {
  LinkedInContentType,
  PlatformContentEnum,
  PlatformItems
} from '@/interface/common';

const normalizeContentType = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const LEGACY_LINKEDIN_POST_TYPES = new Set(
  [
    LinkedInContentType.POST,
    PlatformContentEnum.LINKEDIN_POST,
    'LinkedIn post'
  ].map(normalizeContentType)
);

const MODERN_LINKEDIN_POST_TYPES = new Set(
  [
    LinkedInContentType.IMAGE_TEXT,
    LinkedInContentType.VIDEO,
    PlatformContentEnum.LINKEDIN_IMAGE_POST,
    PlatformContentEnum.LINKEDIN_VIDEO_POST,
    'image post',
    'video post',
    'LinkedIn image post',
    'LinkedIn video post',
    // Keep accepting the former plural English labels while old responses or
    // cached view models may still contain them.
    'image posts',
    'video posts',
    'LinkedIn image posts',
    'LinkedIn video posts'
  ].map(normalizeContentType)
);

/**
 * Treats the original LinkedIn `Post` type as a display fallback.
 *
 * Once either of the newer image/video types is present, legacy Post entries
 * are omitted. A legacy-only order still keeps its Post entry. This helper is
 * intentionally presentation-only: it preserves all other entries, their
 * order and duplicates instead of changing the underlying order data.
 */
export const filterLinkedInLegacyPostFallback = <T>(
  platform: unknown,
  items: readonly T[] | null | undefined,
  getContentType: (item: T) => unknown
): T[] => {
  const result = items ? [...items] : [];
  if (
    String(platform ?? '')
      .trim()
      .toLowerCase() !== PlatformItems.LINKEDIN
  ) {
    return result;
  }

  const hasModernLinkedInType = result.some((item) =>
    MODERN_LINKEDIN_POST_TYPES.has(normalizeContentType(getContentType(item)))
  );
  if (!hasModernLinkedInType) return result;

  return result.filter(
    (item) =>
      !LEGACY_LINKEDIN_POST_TYPES.has(
        normalizeContentType(getContentType(item))
      )
  );
};
